import { useState, useEffect } from 'react';
import {
  Radio, BadgeCheck, Plus, Trash2, RefreshCw, Bell, Users, TrendingUp, TrendingDown,
  Loader2, Save, X, AlertTriangle, CheckCircle, Award, Sparkles,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Spinner, InfoTip } from '../components/ui';
import {
  listSignalChannels, getSignalChannel, upsertMySignalChannel, deleteMySignalChannel,
  subscribeToSignalChannel, unsubscribeFromSignalChannel,
  getSignalsFollowing, getSignalsInbox, getMySignalSubscribers,
  getMyVerifiedCreator, getVerifiedCreatorPolicy, getVerifiedCreatorCalls,
} from '../services/api';

const TABS = [
  { id: 'browse',    label: 'Browse channels',  icon: Radio },
  { id: 'inbox',     label: 'My signals',        icon: Bell },
  { id: 'mychannel', label: 'My channel',        icon: Award },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 5000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}

function fmtPct(v, dp = 1) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(dp)}%`;
}

function VerifiedBadge({ isVerified }) {
  if (!isVerified) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
      <BadgeCheck className="w-3 h-3" /> VERIFIED
      <InfoTip side="bottom">Earned automatically. Requires 20+ decided calls (resolved against real OHLCV) AND 60%+ accuracy AND 30+ days active. Leaderboard ranks by Wilson lower bound so careful high-volume creators outrank lucky low-volume ones.</InfoTip>
    </span>
  );
}

// ─── Browse channels ─────────────────────────────────────────────────────────

function BrowseTab({ onToast }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [busy, setBusy] = useState(null);
  const [following, setFollowing] = useState(new Set());

  async function load() {
    setLoading(true);
    try {
      const [list, fol] = await Promise.all([
        listSignalChannels().catch(() => ({ channels: [] })),
        getSignalsFollowing().catch(() => ({ following: [] })),
      ]);
      const all = list?.data?.channels || list?.channels || [];
      setChannels(all);
      const f = (fol?.data?.following || fol?.following || []).map(x => x.creatorUserId);
      setFollowing(new Set(f));
    } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function handleFollow(ch) {
    setBusy(ch.creatorUserId);
    try {
      await subscribeToSignalChannel(ch.creatorUserId);
      onToast({ type: 'success', message: `Subscribed to ${ch.name}` });
      setFollowing(prev => new Set([...prev, ch.creatorUserId]));
    } catch (err) {
      onToast({ type: 'error', message: err.message });
    }
    setBusy(null);
  }

  async function handleUnfollow(ch) {
    setBusy(ch.creatorUserId);
    try {
      await unsubscribeFromSignalChannel(ch.creatorUserId);
      onToast({ type: 'success', message: `Unsubscribed from ${ch.name}` });
      const next = new Set(following); next.delete(ch.creatorUserId);
      setFollowing(next);
    } catch (err) {
      onToast({ type: 'error', message: err.message });
    }
    setBusy(null);
  }

  const filtered = verifiedOnly ? channels.filter(c => c.creator?.isVerifiedCaller) : channels;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={verifiedOnly} onChange={e => setVerifiedOnly(e.target.checked)}
            className="accent-emerald-500" />
          <span className="text-xs text-gray-400">Verified callers only</span>
        </label>
        <button onClick={load} className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card className="text-center py-16">
          <Radio className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No signal channels yet.</p>
          <p className="text-xs text-gray-600 mt-1">Be the first — open the "My channel" tab.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(ch => {
            const isFollowing = following.has(ch.creatorUserId);
            const cr = ch.creator || {};
            return (
              <Card key={ch.channelId}>
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-white font-semibold truncate">{ch.name}</h3>
                      <VerifiedBadge isVerified={cr.isVerifiedCaller} />
                    </div>
                    <p className="text-xs text-gray-500">by {cr.userName || ch.creatorUserId.slice(0, 12) + '…'}</p>
                  </div>
                  {ch.priceUsdMonthly > 0 ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400">${ch.priceUsdMonthly}/mo</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">FREE</span>
                  )}
                </div>

                {ch.description && <p className="text-xs text-gray-400 mb-3">{ch.description}</p>}

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-dark-700/40 rounded-lg p-2 text-center">
                    <p className="text-[9px] uppercase text-gray-500">Accuracy</p>
                    <p className="text-sm font-bold text-white">{fmtPct(cr.accuracy)}</p>
                  </div>
                  <div className="bg-dark-700/40 rounded-lg p-2 text-center">
                    <p className="text-[9px] uppercase text-gray-500">Decided</p>
                    <p className="text-sm font-bold text-white">{cr.decidedCount ?? 0}</p>
                  </div>
                  <div className="bg-dark-700/40 rounded-lg p-2 text-center">
                    <p className="text-[9px] uppercase text-gray-500">Subscribers</p>
                    <p className="text-sm font-bold text-white">{ch.subscriberCount}</p>
                  </div>
                </div>

                {cr.accuracyCi95 && (
                  <p className="text-[10px] text-gray-600 mb-3">
                    95% CI: <span className="font-mono">{cr.accuracyCi95.low}% – {cr.accuracyCi95.high}%</span>
                  </p>
                )}

                {isFollowing ? (
                  <button onClick={() => handleUnfollow(ch)} disabled={busy === ch.creatorUserId}
                    className="w-full px-3 py-2 rounded-xl text-xs font-medium text-gray-400 bg-dark-700 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40 flex items-center justify-center gap-1.5">
                    {busy === ch.creatorUserId ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    Unsubscribe
                  </button>
                ) : (
                  <button onClick={() => handleFollow(ch)} disabled={busy === ch.creatorUserId}
                    className="w-full px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 flex items-center justify-center gap-1.5">
                    {busy === ch.creatorUserId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Subscribe
                  </button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── My signals (subscriber inbox) ───────────────────────────────────────────

function InboxTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await getSignalsInbox(100);
      setItems(res?.data?.inbox || res?.inbox || []);
    } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>;

  if (items.length === 0) {
    return (
      <Card className="text-center py-16">
        <Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-sm text-gray-400">No signals delivered yet.</p>
        <p className="text-xs text-gray-600 mt-1">Subscribe to a channel; new auto-detected calls appear here within ~1 minute.</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Recent signals</h3>
        <button onClick={load} className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const Icon = it.action === 'bullish' ? TrendingUp : TrendingDown;
          const colorIcon = it.action === 'bullish' ? 'text-emerald-400' : 'text-red-400';
          return (
            <div key={`${it.callId}-${i}`} className="flex items-center gap-3 bg-dark-700/40 rounded-xl p-3">
              <Icon className={`w-4 h-4 ${colorIcon}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-xs font-bold ${colorIcon} uppercase`}>{it.action === 'bullish' ? 'BUY' : 'SELL'}</span>
                  <span className="text-sm text-white font-mono">${it.token}</span>
                  {it.entryPrice != null && <span className="text-xs text-gray-500">@ ${Number(it.entryPrice).toFixed(4)}</span>}
                </div>
                <p className="text-[11px] text-gray-500 truncate">{it.callContent}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-gray-600">{new Date(it.deliveredAt).toLocaleString()}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── My channel (creator dashboard + editor) ─────────────────────────────────

function MyChannelTab({ onToast }) {
  const [record, setRecord] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const [me, pol] = await Promise.all([
        getMyVerifiedCreator().catch(() => null),
        getVerifiedCreatorPolicy().catch(() => null),
      ]);
      const rec = me?.data?.record || me?.record || null;
      setRecord(rec);
      setPolicy(pol?.data || pol);
      // Load my recent calls for transparency
      if (rec?.userId) {
        try {
          const calls = await getVerifiedCreatorCalls(rec.userId, 20);
          setRecentCalls(calls?.data?.calls || calls?.calls || []);
        } catch {}
      }
      // Load my channel + subscribers
      try {
        const ch = rec?.userId ? await getSignalChannel(rec.userId) : null;
        const c = ch?.data?.channel || ch?.channel || null;
        setChannel(c);
        if (c) {
          setName(c.name || '');
          setDescription(c.description || '');
          setEnabled(c.enabled !== false);
        }
        const subs = await getMySignalSubscribers().catch(() => null);
        setSubscribers(subs?.data?.subscribers || subs?.subscribers || []);
      } catch {}
    } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await upsertMySignalChannel({ name, description, enabled });
      onToast({ type: 'success', message: 'Channel saved' });
      setEditing(false);
      await load();
    } catch (err) {
      onToast({ type: 'error', message: err.message });
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!window.confirm('Delete your signal channel? Subscribers will stop receiving your calls.')) return;
    try {
      await deleteMySignalChannel();
      onToast({ type: 'success', message: 'Channel deleted' });
      setChannel(null);
      setName(''); setDescription('');
      await load();
    } catch (err) {
      onToast({ type: 'error', message: err.message });
    }
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>;

  const elig = record?.eligibility;

  return (
    <div className="space-y-4">
      {/* Track record */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-400" /> Your verified-caller progress
          </h3>
          {record?.isVerifiedCaller && (
            <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              <BadgeCheck className="w-3.5 h-3.5" /> VERIFIED CALLER
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Thresholds come from the live policy. We only show the "need X" hint
              once the policy actually loaded — never hardcode fallback numbers
              because they could drift from what the backend currently enforces. */}
          <div className="bg-dark-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Decided calls</p>
            <p className="text-lg font-bold text-white">{record?.decidedCount ?? 0}</p>
            {policy?.data?.thresholds?.minDecidedCalls != null && (
              <p className="text-[10px] text-gray-600">need {policy.data.thresholds.minDecidedCalls}</p>
            )}
          </div>
          <div className="bg-dark-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Accuracy</p>
            <p className="text-lg font-bold text-white">{fmtPct(record?.accuracy)}</p>
            {policy?.data?.thresholds?.minAccuracyPct != null && (
              <p className="text-[10px] text-gray-600">need ≥{policy.data.thresholds.minAccuracyPct}%</p>
            )}
          </div>
          <div className="bg-dark-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Days active</p>
            <p className="text-lg font-bold text-white">{record?.daysActive?.toFixed(0) ?? 0}</p>
            {policy?.data?.thresholds?.minDaysActive != null && (
              <p className="text-[10px] text-gray-600">need ≥{policy.data.thresholds.minDaysActive}d</p>
            )}
          </div>
          <div className="bg-dark-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Avg return</p>
            <p className="text-lg font-bold text-white">{fmtPct(record?.avgReturnPct, 2)}</p>
            <p className="text-[10px] text-gray-600">per decided call</p>
          </div>
        </div>
        {record?.accuracyCi95 && record?.decidedCount > 0 && (
          <p className="text-[10px] text-gray-500 mt-3">
            95% confidence interval: <span className="font-mono">{record.accuracyCi95.low}% – {record.accuracyCi95.high}%</span>
          </p>
        )}
        {elig && !record?.isVerifiedCaller && (
          <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-xl px-3 py-2 text-[11px] text-amber-300/80">
            <p className="font-semibold mb-1">To earn the Verified Caller badge:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li className={elig.meetsCallCount ? 'text-emerald-400' : ''}>{elig.meetsCallCount ? '✓' : '○'} ≥{elig.thresholds.minDecidedCalls} decided calls</li>
              <li className={elig.meetsAccuracy  ? 'text-emerald-400' : ''}>{elig.meetsAccuracy  ? '✓' : '○'} ≥{elig.thresholds.minAccuracyPct}% accuracy</li>
              <li className={elig.meetsDaysActive ? 'text-emerald-400' : ''}>{elig.meetsDaysActive ? '✓' : '○'} ≥{elig.thresholds.minDaysActive} days from first to most recent call</li>
            </ul>
          </div>
        )}
      </Card>

      {/* Channel editor — header changes based on whether the user has a channel,
          is creating one for the first time, or already owns one. */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Radio className="w-4 h-4 text-emerald-400" />
            {channel ? 'Your signal channel' : editing ? 'Create your signal channel' : 'Your signal channel'}
          </h3>
          <div className="flex items-center gap-3">
            {channel && (
              <span className="text-[10px] text-gray-500">{subscribers.length} subscriber{subscribers.length === 1 ? '' : 's'}</span>
            )}
            {editing && !channel && (
              <button onClick={() => { setEditing(false); setName(''); setDescription(''); }}
                className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            )}
          </div>
        </div>

        {!channel && !editing && (
          <div className="text-center py-8">
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-sm text-gray-300">You don't have a signal channel yet.</p>
            <p className="text-xs text-gray-500 mt-1 mb-1">Your calls are <strong className="text-emerald-400">already being tracked</strong> toward verified-caller eligibility (see "Your recent calls" below) — you don't need a channel for that.</p>
            <p className="text-xs text-gray-500 mt-1 mb-4">A channel is the <strong className="text-white">distribution layer</strong> — open one to let other users subscribe and receive your calls automatically.</p>
            <button onClick={() => setEditing(true)}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Open my channel
            </button>
          </div>
        )}

        {(channel || editing) && (
          <div className="space-y-3">
            {/* Mini-explainer for first-time channel creation */}
            {editing && !channel && (
              <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-xl px-3 py-2.5 text-xs text-emerald-300/90">
                <p className="font-semibold mb-1">What happens when you click Create</p>
                <ul className="space-y-0.5 list-disc list-inside text-emerald-300/80">
                  <li>Other users can find your channel in the <strong>Browse channels</strong> tab</li>
                  <li>Every trade call you post in chat will auto-distribute to your subscribers within ~1 minute</li>
                  <li>Your existing <strong>{recentCalls.length} recorded call{recentCalls.length === 1 ? '' : 's'}</strong> become visible on your channel's public page</li>
                  <li>The VERIFIED badge appears once you meet all three thresholds above</li>
                </ul>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Channel name</label>
              <input value={name} onChange={e => setName(e.target.value)} maxLength={80}
                placeholder="e.g. Daily ETH calls"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Description (optional)</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={500} rows={3}
                placeholder="What kind of calls do you make? Time horizon? Asset focus?"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
                className="accent-emerald-500" />
              <span className="text-xs text-gray-400">Channel enabled (subscribers receive your calls)</span>
            </label>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !name.trim()}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {channel ? 'Update' : 'Create'}
              </button>
              {editing && !channel && (
                <button onClick={() => { setEditing(false); setName(''); setDescription(''); }}
                  className="px-3 py-2 rounded-xl text-xs text-gray-400 border border-dark-600 hover:text-white">
                  Cancel
                </button>
              )}
              {channel && (
                <button onClick={handleDelete}
                  className="px-3 py-2 rounded-xl text-xs text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Delete channel
                </button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Recent calls (transparency) */}
      {recentCalls.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Your recent calls</h3>
          <div className="space-y-1.5">
            {recentCalls.map(c => {
              const status = c.finalOutcome === 'hit' ? { color: 'text-emerald-400', label: 'HIT' }
                : c.finalOutcome === 'miss'    ? { color: 'text-red-400',     label: 'MISS' }
                : c.finalOutcome === 'unknown' ? { color: 'text-gray-500',    label: 'UNPRICED' }
                : { color: 'text-amber-400', label: 'PENDING' };
              return (
                <div key={c.callId} className="flex items-center gap-2 text-xs bg-dark-700/30 rounded-lg px-3 py-1.5">
                  <span className={`uppercase font-bold w-12 ${c.action === 'bullish' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {c.action === 'bullish' ? 'BUY' : 'SELL'}
                  </span>
                  <span className="text-white font-mono">${c.token}</span>
                  <span className="text-gray-500 flex-1 truncate">{new Date(c.callTimestamp).toLocaleString()}</span>
                  <span className={`text-[10px] font-bold ${status.color}`}>{status.label}</span>
                  {c.finalResolution?.percentChange != null && (
                    <span className={`text-[10px] font-mono ${c.finalResolution.percentChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {c.finalResolution.percentChange >= 0 ? '+' : ''}{c.finalResolution.percentChange.toFixed(2)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function SignalChannelsPage() {
  const [activeTab, setActiveTab] = useState('browse');
  const [toast, setToast] = useState(null);
  const { isLoggedIn } = useAuth();

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
          <Radio className="w-6 h-6" /> Signal Channels
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            VERIFIED-BACKED
          </span>
        </h1>
        <p className="text-gray-400 text-sm mt-1 max-w-3xl leading-relaxed">
          Subscribe to creators whose call accuracy is verified against real on-chain prices.
          Every call from a creator you follow lands in your inbox within ~1 minute. <strong className="text-emerald-400">No paraphrasing, no marketing — just the real auto-detected call.</strong>
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-800 p-1 rounded-xl border border-dark-700/50 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-1 justify-center ${
              activeTab === id ? 'bg-accent-primary text-dark-900' : 'text-gray-400 hover:text-white hover:bg-dark-700/60'
            }`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'browse'    && <BrowseTab    onToast={setToast} />}
      {activeTab === 'inbox'     && <InboxTab />}
      {activeTab === 'mychannel' && <MyChannelTab onToast={setToast} />}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
